import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { shallowEqual } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { getClientSettings } from '@wg/web2clientapi/browser/getClientSettings';
import { updateBrowserControlState } from '@wg/web2clientapi/browser/updateBrowserControlState';
import { ErrorLoad } from '@wg/wows-react-uikit';
import { OnboardingProvider } from '@wg/wows-react-uikit/features/onboarding/provider/onboardingProvider';

import { fetchOnboardingCompletedSteps, fetchOnboardingCompleteSteps } from '~/api/onboarding';
import { onboardingStepsList } from '~/const/onboarding';
import { isInGame, isWCFRequired } from '~/constants';
import { logPageView } from '~/helpers/analytics';
import { t } from '~/helpers/localization';
import useLocationChange from '~/hooks/useLocationChange';
import useMount from '~/hooks/useMount';
import useRouteHandlePropChange from '~/hooks/useRouteHandlePropChange';
import { RefKeys, refManager } from '~/managers/refManager';
import NavigationGuard from '~/router/NavigationGuard';
import { useAppDispatch, useAppSelector } from '~/store';
import { actionsAccount } from '~/Actions/ActionAccount';

import { IsFromSearchContext } from '~/Providers/IsFromSearch';
import DialogsManager from '~/Components/Managers/DialogsManager/DialogsManager';
import { NavMenu, ViewBackground } from '~/UIKit';

import styles from './Layout.scss';

import type { RootState } from '~/store';

const bodyClassName = isInGame ? 'ingame' : 'not-ingame';

const stateSelector = (state: RootState) => {
  return {
    currentAccount: state.currentAccount,
  };
};

const Layout: React.FC = () => {
  const { currentAccount } = useAppSelector(stateSelector, shallowEqual);
  const dispatch = useAppDispatch();

  const appRef = refManager.getRef(RefKeys.APP);

  useRouteHandlePropChange('title', (title) => {
    document.title = title?.() || t('Кланы');
  });

  const location = useLocation();
  const isOpenedFromSearch = location.pathname.includes('clan-profile');

  useLocationChange(() => {
    logPageView();

    if (isWCFRequired && appRef.current) {
      appRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  const [onboardingCompletedSteps, setOnboardingCompletedSteps] = useState<Nullable<string[]>>(null);

  const getUserGraphicsSettings = useCallback(async () => {
    try {
      const response = await getClientSettings();
      if (response.data) {
        dispatch(actionsAccount.setClientSettings(response.data));
      }
    } catch (e) {
      console.error('Unable to get client data', e);
    }
  }, [dispatch]);

  useMount(() => {
    document.body.className = bodyClassName;

    if (isInGame) {
      void getUserGraphicsSettings();
    }

    if (!currentAccount.id) {
      return;
    }

    void fetchOnboardingCompletedSteps().then((steps) => {
      if (!steps) {
        return;
      }
      setOnboardingCompletedSteps(steps);
    });
  });

  const completeSteps = (stepsNames: string[]) => {
    if (!currentAccount.id) {
      return;
    }
    void fetchOnboardingCompleteSteps(stepsNames);
  };

  const appContainer = useRef<HTMLElement>(document.getElementById('app'));
  const contentContainer = useRef<HTMLElement>(null);

  const [showShadowTop, setShowShadowTop] = useState(false);
  const [shadowWidth, setShadowWidth] = useState(0);
  const [shadowLeft, setShadowLeft] = useState(0);

  useMount(() => {
    updateShadows();

    appContainer.current?.addEventListener('scroll', updateShadows);
    window.addEventListener('resize', updateShadows);

    return () => {
      appContainer.current?.removeEventListener('scroll', updateShadows);
      window.removeEventListener('resize', updateShadows);
    };
  });

  const updateShadows = () => {
    if (!appContainer.current) {
      return;
    }

    setShowShadowTop(appContainer.current.scrollTop > 0);

    const contentRect = contentContainer.current?.getBoundingClientRect();
    if (contentRect) {
      setShadowWidth(contentRect.width + 16);
      setShadowLeft(contentRect.left - 8);
    }
  };

  const classNameShadowTop = classNames(styles.shadowTop, {
    [styles.isVisible]: showShadowTop,
  });

  const shadowStyle = {
    left: shadowLeft,
    width: shadowWidth,
  };

  const renderError = useCallback(() => {
    void updateBrowserControlState(false);

    return (
      <ErrorLoad
        isFlat
        key="error-load"
        message={t('Произошла ошибка. Повторите попытку позже')}
        onReloadClick={() => window.location.reload()}
      />
    );
  }, []);

  return (
    <OnboardingProvider
      steps={onboardingStepsList}
      completedSteps={onboardingCompletedSteps}
      completeSteps={completeSteps}
    >
      <IsFromSearchContext.Provider value={isOpenedFromSearch}>
        <ErrorBoundary fallbackRender={renderError}>
          <ViewBackground />
          <main className={styles.main}>
            <NavMenu />
            <section className={styles.section} ref={contentContainer}>
              <div className={classNameShadowTop} style={shadowStyle} />
              <NavigationGuard>
                <Outlet />
              </NavigationGuard>
            </section>
          </main>
        </ErrorBoundary>
        <DialogsManager />
      </IsFromSearchContext.Provider>
    </OnboardingProvider>
  );
};

export default Layout;
